import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, Hourglass, BadgeCheck, TrendingUp, Check, X } from "lucide-react";
import { StatusPill } from "@/components/billing/OrderHistory";
import api from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export function PaymentsPanel() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending_verification");

  const load = useCallback(() => {
    api.get("/admin/payments/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/admin/payments", { params: filter === "all" ? {} : { status: filter } }).then((r) => setOrders(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/payments/${id}/${action}`, action === "reject" ? { reason: "UTR could not be matched to a UPI credit" } : {});
      toast.success(action === "approve" ? "Pack activated for user" : "Payment rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const setProfile = async (quota_profile) => {
    try {
      await api.put("/admin/billing/settings", { quota_profile, reminder_days: summary?.settings?.reminder_days || 3 });
      toast.success(`Quota profile → ${quota_profile}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const runReminders = async () => {
    try { const r = await api.post("/admin/billing/reminders/run"); toast.success(`Reminders: ${r.data.sent} sent · ${r.data.due} due`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cards = summary ? [
    { label: "UPI Revenue", value: inr(summary.revenue), sub: `${summary.approved_count} approved · ${summary.payee.vpa}`, icon: IndianRupee, color: "text-emerald-400" },
    { label: "Pending Verification", value: summary.pending_count, sub: `${inr(summary.pending_amount)} awaiting approval`, icon: Hourglass, color: "text-amber-400" },
    { label: "Active Packs", value: summary.active_subscriptions, sub: "Live prepaid subscriptions", icon: BadgeCheck, color: "text-cyan-400" },
    { label: "Est. AI Cost vs Spend", value: inr(summary.ai_cost), sub: summary.margin_pct === null ? "No revenue yet" : `Gross margin ${summary.margin_pct}% (${inr(summary.gross_margin)})`, icon: TrendingUp, color: summary.gross_margin >= 0 ? "text-purple-400" : "text-red-400" },
  ] : [];

  return (
    <div data-testid="payments-panel" className="space-y-4 mb-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={c.label} data-testid={`payment-stat-${i}`} className="glass rounded-2xl p-5">
            <c.icon size={19} className={`${c.color} mb-2`} />
            <div className="text-2xl font-display font-extrabold font-mono">{c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            <div className="text-[10px] text-slate-600 mt-0.5 truncate">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display font-semibold text-sm">UPI Payments · UTR Verification</div>
            <div className="flex gap-1">
              {[["pending_verification", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([k, l]) => (
                <button key={k} data-testid={`payments-filter-${k}`} onClick={() => setFilter(k)} className={`text-[10px] font-mono px-2 py-1 rounded-md border ${filter === k ? "border-purple-500/40 bg-purple-500/15 text-purple-200" : "border-white/10 text-slate-500 hover:text-white"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] font-mono uppercase text-slate-500 border-b border-white/10">
                <th className="text-left px-4 py-2">Order</th><th className="text-left px-4 py-2">User</th><th className="text-left px-4 py-2">Pack</th><th className="text-left px-4 py-2">UTR</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2" />
              </tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-slate-500" data-testid="payments-empty">No payments in this view</td></tr>}
                {orders.map((o, i) => (
                  <tr key={o.order_id} data-testid={`payment-row-${i}`} className="border-b border-white/5">
                    <td className="px-4 py-2.5 font-mono text-xs text-purple-300">{o.order_id}</td>
                    <td className="px-4 py-2.5 text-xs"><div>{o.user_name}</div><div className="text-slate-500 font-mono">{o.user_email}</div></td>
                    <td className="px-4 py-2.5 text-xs">{o.pack_name} <span className="font-mono text-slate-400">{inr(o.amount)}</span></td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-300">{o.utr || "—"}</td>
                    <td className="px-4 py-2.5"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {o.status === "pending_verification" && (
                        <>
                          <button data-testid={`approve-${i}`} onClick={() => act(o.order_id, "approve")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 mr-1"><Check size={12} /> Approve</button>
                          <button data-testid={`reject-${i}`} onClick={() => act(o.order_id, "reject")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20"><X size={12} /> Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="pack-economics">
          <div className="font-display font-semibold text-sm">Pack Economics · Spend vs Cost</div>
          <div data-testid="quota-profile-switch" className="mt-3 mb-3 flex flex-wrap gap-1.5">
            {(summary?.quota_profiles || []).map((p) => (
              <button key={p.id} data-testid={`quota-profile-${p.id}`} onClick={() => setProfile(p.id)}
                className={`text-[10px] font-mono px-2.5 py-1 rounded-md border ${summary?.settings?.quota_profile === p.id ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-slate-500 hover:text-white"}`}>{p.label}</button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 mb-3">Provider cost at full quota use (video {inr(summary?.unit_cost_inr?.video_min)}/min · voice {inr(summary?.unit_cost_inr?.voice_min)}/min · image {inr(summary?.unit_cost_inr?.image)}). Break-even = % of quota a buyer can consume before the pack loses money.</div>
          <div className="space-y-3">
            {(summary?.pack_economics || []).map((p) => (
              <div key={p.pack} data-testid={`economics-${p.pack.toLowerCase().replace(/\s/g, "-")}`} className="rounded-xl border border-white/10 p-3">
                <div className="flex items-center justify-between"><span className="font-semibold text-sm">{p.pack}</span><span className="font-mono text-xs text-slate-300">{inr(p.price)}</span></div>
                <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className={`h-full ${p.breakeven_utilization_pct < 50 ? "bg-red-500" : "bg-gradient-to-r from-purple-500 to-cyan-500"}`} style={{ width: `${Math.min(100, p.breakeven_utilization_pct)}%` }} /></div>
                <div className="flex justify-between text-[10px] font-mono mt-1.5">
                  <span className="text-slate-500">{p.quota_label}</span>
                  <span className={p.breakeven_utilization_pct < 50 ? "text-red-400" : "text-emerald-400"}>break-even {p.breakeven_utilization_pct}%</span>
                </div>
                <div className="text-[10px] font-mono text-slate-600 mt-0.5">cost at full use {inr(p.cost_at_full_use)} · margin {inr(p.margin_at_full_use)}</div>
              </div>
            ))}
          </div>
          {summary && (
            <div className="mt-3 text-[10px] font-mono text-slate-500">
              Consumed · {Math.round((summary.consumed?.video_sec || 0) / 60)} video min · {Math.round((summary.consumed?.voice_sec || 0) / 60)} voice min · {summary.consumed?.images || 0} images
              <div data-testid="telegram-status" className={`mt-1 ${summary.telegram_alerts ? "text-emerald-400" : "text-amber-400"}`}>Telegram alerts: {summary.telegram_alerts ? "ON" : "not configured"}</div>
              <div data-testid="email-status" className={`${summary.email_alerts ? "text-emerald-400" : "text-amber-400"}`}>Email reminders (Resend): {summary.email_alerts ? "ON" : "not configured"}</div>
              <button data-testid="run-reminders" onClick={runReminders} className="mt-2 text-[10px] font-mono px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:bg-white/5">Run expiry reminders now ({summary.settings?.reminder_days}d window)</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
